import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapPin, BookOpen } from "lucide-react";

const TRACK_ACCENTS = {
  foundations: "bg-[#0A0A0A] text-white",
  india: "bg-[#C5A059] text-[#0A0A0A]",
  dubai: "bg-white text-[#0A0A0A] border border-[#0A0A0A]",
};

export default function Modules() {
  const [tracks, setTracks] = useState([]);
  const [progress, setProgress] = useState({});
  const [filter, setFilter] = useState("all");
  const { user } = useAuth();
  const loc = useLocation();

  useEffect(() => {
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {});
    if (user && user !== false) {
      api.get("/progress").then((r) => {
        const map = {};
        r.data.modules.forEach((m) => { map[m.module_id] = m; });
        setProgress(map);
      }).catch(() => {});
    }
  }, [user]);

  // Support anchor scroll (?filter or #india / #dubai)
  useEffect(() => {
    if (loc.hash) {
      const el = document.getElementById(loc.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [loc.hash, tracks]);

  const visibleTracks = useMemo(() => {
    if (filter === "all") return tracks;
    return tracks.filter((t) => t.id === filter);
  }, [tracks, filter]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="mb-10 max-w-3xl">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-3">Curriculum</div>
        <h1 data-testid="modules-title" className="font-serif text-5xl lg:text-6xl text-[#0A0A0A] font-black tracking-tight leading-none mb-6">
          Choose your track.
        </h1>
        <p className="text-lg text-[#525252] leading-relaxed">
          Foundations sets the mental model. India and Dubai are separate tracks — same asset class, different playbook.
        </p>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-12" data-testid="track-filter">
        {[
          { id: "all", label: "All" },
          ...tracks.map((t) => ({ id: t.id, label: t.label })),
        ].map((opt) => (
          <button
            key={opt.id}
            data-testid={`filter-${opt.id}`}
            onClick={() => setFilter(opt.id)}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border transition-colors ${
              filter === opt.id
                ? "border-[#0A0A0A] bg-[#0A0A0A] text-[#FFFFFF]"
                : "border-[#E5E5E0] text-[#525252] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {visibleTracks.map((track) => (
        <section id={track.id} key={track.id} className="mb-16" data-testid={`track-${track.id}`}>
          <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${TRACK_ACCENTS[track.id]}`}>
                  {track.label}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#525252]">{track.modules.length} modules</span>
              </div>
              <h2 className="font-serif text-3xl lg:text-4xl text-[#0A0A0A] font-bold tracking-tight max-w-2xl">{track.tagline}</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 border-t border-l border-[#E5E5E0]" data-testid={`modules-grid-${track.id}`}>
            {track.modules.map((m) => {
              const p = progress[m.id];
              return (
                <Link
                  key={m.id}
                  data-testid={`module-card-${m.id}`}
                  to={`/modules/${m.id}`}
                  className="group relative p-10 border-r border-b border-[#E5E5E0] hover:bg-[#F5F5F0] transition-colors block"
                >
                  <div className="flex items-start justify-between mb-8">
                    <div className="font-mono text-5xl font-medium text-[#C5A059]">{m.number}</div>
                    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[#525252]">
                      <MapPin className="w-3 h-3" /> {m.region}
                    </div>
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-[#0A0A0A] mb-2 leading-tight">{m.title}</h3>
                  <p className="text-[#525252] italic mb-4 font-serif text-sm">{m.subtitle}</p>
                  <p className="text-sm text-[#525252] leading-relaxed mb-6 line-clamp-4">{m.summary}</p>
                  <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider text-[#525252] mb-3">
                    <span className="flex items-center gap-1.5"><BookOpen className="w-3 h-3" /> {m.chapter_count} chapters</span>
                    <span>{m.reading_time} min read</span>
                  </div>
                  {p && p.total > 0 && (
                    <div>
                      <div className="progress-rail"><div className="progress-fill-gold progress-fill" style={{ width: `${p.percent}%` }} /></div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-[#525252] mt-2">
                        {p.completed} of {p.total} complete · {p.percent}%
                      </div>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
